import type {
	StructuredAddress,
	UnstructuredAddress,
} from '@api/hooks/types/createTransaction.ts';

export type AddressMode = 'STRUCTURED' | 'UNSTRUCTURED';

type AnyAddress =
	| (Partial<Omit<StructuredAddress, 'type'>> &
			Partial<Omit<UnstructuredAddress, 'type'>> & { type?: AddressMode })
	| undefined;

const OLD_ADDRESS_FIELDS = [
	'country',
	'town',
	'line',
	'building_number',
	'postal_code',
] as const;

const NEW_ADDRESS_FIELDS = [
	'country',
	'town',
	'street_name',
	'building_number',
	'building_name',
	'postal_code',
] as const;

export const STRUCTURED_ONLY_FIELDS = ['town', 'building_number', 'postal_code'] as const;

// Remove after FF removed
export const isStructuredComplete = (address: AnyAddress): boolean => {
	if (!address) {
		return false;
	}
	return OLD_ADDRESS_FIELDS.every((field) => !!address[field]);
};

export const isAddressEmpty = (
	address: AnyAddress,
	hasAddressAligned?: boolean,
): boolean =>
	!address ||
	(hasAddressAligned ? NEW_ADDRESS_FIELDS : OLD_ADDRESS_FIELDS).every(
		(field) => !address[field],
	);

export const resolveAddressMode = (
	address: AnyAddress,
	hasAddressAligned?: boolean,
): AddressMode => {
	if (hasAddressAligned || isAddressEmpty(address, hasAddressAligned)) {
		return 'STRUCTURED';
	}
	return isStructuredComplete(address) ? 'STRUCTURED' : 'UNSTRUCTURED';
};

// ToDo: remove after BT-996-structured-addresses-in-ib-payments
export const applyAddressMode = <T extends NonNullable<AnyAddress>>(
	address: T,
	hasAddessAligned?: boolean,
): T => {
	const mutable = address as NonNullable<AnyAddress>;
	mutable.type = resolveAddressMode(mutable, hasAddessAligned);
	if (mutable.type === 'UNSTRUCTURED') {
		STRUCTURED_ONLY_FIELDS.forEach((field) => {
			delete mutable[field];
		});
	}
	return address;
};

type AddressOwners = {
	beneficiary?: { address?: AnyAddress; bank?: { address?: AnyAddress } };
	correspondent?: { address?: AnyAddress };
};

// ToDo: remove after BT-996-structured-addresses-in-ib-payments
export const resolveTemplateAddressModes = <T extends AddressOwners | undefined>(
	template: T,
	hasAddressAligned?: boolean,
): T => {
	if (!template) {
		return template;
	}
	const next = structuredClone(template);
	const setMode = (owner?: { address?: AnyAddress }) => {
		if (owner?.address) {
			owner.address.type = resolveAddressMode(owner.address, hasAddressAligned);
		}
	};
	setMode(next.beneficiary);
	setMode(next.beneficiary?.bank);
	setMode(next.correspondent);
	return next;
};
